import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { api } from '../../contexts/AuthContext';
import { isAbortError, extractApiError } from '../../api/clusters';
import { Button } from '../../components/ui/button';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import BriefCard, { Brief } from './components/BriefCard';
import NewBriefModal, { NewBriefPayload } from './components/NewBriefModal';
import { usePortalSlot } from '../../hooks/usePortalSlot';

/** Matches the backend's LIST_DEFAULT_LIMIT (briefsController.ts). */
const PAGE_SIZE = 100;

interface BriefsListResponse {
    briefs: Brief[];
    pagination?: { limit: number; offset: number; total: number };
}

/**
 * "My Briefs" page (deepcontext-d68.10) — Universe sidebar sub-page.
 *
 * - Lists briefs for the requester's company (server scopes by JWT companyId).
 * - "Create Brief" opens a modal and POSTs to /api/clusters/briefs.
 * - Each card has "Open in Universe", which navigates to the Universe
 *   overview with the brief text in router state so the overview can
 *   pre-load it as the anchor / scoring input.
 * - The list is paginated: GET sends an explicit limit/offset and the page
 *   reads `pagination.total` from the envelope. The per-company quota allows
 *   up to 500 briefs while the server returns 100 per page, so beyond the
 *   first page we render an "X of Y" counter plus a load-more control
 *   instead of silently truncating (bd deepcontext-rhku).
 *
 * deepcontext-736j: this subroute drops the shared Clusters market-selector
 * chrome (briefs are company-wide, not market-scoped — see ClustersTopBar)
 * and portals its own "Create Brief" action into the same `app-page-actions`
 * slot, on the same row as the page-specific subtitle (see pageTitles.ts).
 */
const BriefsPage: React.FC = () => {
    const navigate = useNavigate();
    const [briefs, setBriefs] = useState<Brief[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingBrief, setEditingBrief] = useState<Brief | null>(null);
    const actionsSlot = usePortalSlot('app-page-actions');

    // Controller for an in-flight "load more" request, so it can be cancelled
    // on unmount the same way the initial fetch is.
    const loadMoreController = useRef<AbortController | null>(null);

    const fetchBriefs = useCallback(async (signal?: AbortSignal) => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.get<BriefsListResponse>(
                '/api/clusters/briefs',
                { params: { limit: PAGE_SIZE, offset: 0 }, signal },
            );
            if (signal?.aborted) return;
            const page = response.data.briefs ?? [];
            setBriefs(page);
            // Older responses (and tests) may omit the envelope — fall back to
            // what we actually received so no phantom load-more appears.
            setTotal(response.data.pagination?.total ?? page.length);
        } catch (err: any) {
            if (isAbortError(err) || signal?.aborted) return;
            setError(extractApiError(err, 'Failed to load briefs'));
        } finally {
            if (!signal?.aborted) setLoading(false);
        }
    }, []);

    // Cancel the briefs fetch when the user switches Universe tabs or
    // navigates away mid-flight (bd deepcontext-26s).
    useEffect(() => {
        const controller = new AbortController();
        fetchBriefs(controller.signal);
        return () => {
            controller.abort();
            loadMoreController.current?.abort();
        };
    }, [fetchBriefs]);

    const handleLoadMore = async () => {
        if (loadingMore) return;
        const controller = new AbortController();
        loadMoreController.current = controller;
        const signal = controller.signal;
        const offset = briefs.length;
        setLoadingMore(true);
        setError(null);
        try {
            const response = await api.get<BriefsListResponse>(
                '/api/clusters/briefs',
                { params: { limit: PAGE_SIZE, offset }, signal },
            );
            if (signal.aborted) return;
            const page = response.data.briefs ?? [];
            // Append, de-duplicating by id: a brief created or deleted between
            // pages shifts the server-side window and can repeat a row.
            setBriefs((prev) => {
                const seen = new Set(prev.map((b) => b.id));
                return [...prev, ...page.filter((b) => !seen.has(b.id))];
            });
            const reported = response.data.pagination?.total;
            if (typeof reported === 'number') setTotal(reported);
        } catch (err: any) {
            if (isAbortError(err) || signal.aborted) return;
            setError(extractApiError(err, 'Failed to load briefs'));
        } finally {
            if (!signal.aborted) setLoadingMore(false);
            if (loadMoreController.current === controller) {
                loadMoreController.current = null;
            }
        }
    };

    const handleCreate = async (payload: NewBriefPayload) => {
        const response = await api.post<{ brief: Brief }>(
            '/api/clusters/briefs',
            payload
        );
        setBriefs((prev) => [response.data.brief, ...prev]);
        setTotal((prev) => prev + 1);
    };

    const handleEdit = (brief: Brief) => {
        setEditingBrief(brief);
    };

    const handleUpdate = async (payload: NewBriefPayload) => {
        if (!editingBrief) return;
        const response = await api.put<{ brief: Brief }>(
            `/api/clusters/briefs/${editingBrief.id}`,
            payload
        );
        setBriefs((prev) =>
            prev.map((b) => (b.id === response.data.brief.id ? response.data.brief : b)),
        );
    };

    const handleDelete = async (brief: Brief) => {
        const confirmed = window.confirm(`Delete brief "${brief.name}"?`);
        if (!confirmed) return;
        try {
            await api.delete(`/api/clusters/briefs/${brief.id}`);
            setBriefs((prev) => prev.filter((b) => b.id !== brief.id));
            setTotal((prev) => Math.max(0, prev - 1));
        } catch (err: any) {
            setError(extractApiError(err, 'Failed to delete brief'));
        }
    };

    const handleOpenInClusters = (brief: Brief) => {
        navigate('../overview', {
            relative: 'path',
            state: {
                briefId: brief.id,
                briefName: brief.name,
                briefText: brief.brief_text,
            },
        });
    };

    return (
        <div className="space-y-4" data-testid="briefs-page">
            {/* deepcontext-736j: subtitle comes from AppLayout's per-page
                header (pageTitles.ts's /clusters/briefs entry) — this page
                only owns the CTA, portaled onto that same actions row. No
                market selector here (ClustersTopBar hides itself on this
                subroute): briefs are company-wide, not market-scoped. */}
            {actionsSlot &&
                createPortal(
                    <Button
                        onClick={() => setModalOpen(true)}
                        data-testid="briefs-page-new-button"
                    >
                        Create Brief
                    </Button>,
                    actionsSlot,
                )}

            {error && (
                <div
                    className="rounded-md border border-risk bg-risk-bg px-3 py-2 text-sm text-risk"
                    role="alert"
                    data-testid="briefs-page-error"
                >
                    {error}
                </div>
            )}

            {loading ? (
                <div
                    className="text-sm text-ink-muted"
                    data-testid="briefs-page-loading"
                >
                    Loading briefs…
                </div>
            ) : briefs.length === 0 ? (
                <div
                    className="rounded-2xl border-[1.5px] border-dashed border-line px-6 py-8 flex flex-col items-center gap-2 text-center"
                    data-testid="briefs-page-empty"
                >
                    <div className="w-10 h-10 rounded-2xl bg-nav-active flex items-center justify-center">
                        <DocumentTextIcon className="h-5 w-5 text-accent-active" />
                    </div>
                    <p className="text-sm font-bold text-ink">No briefs yet</p>
                    <p className="text-xs text-ink-muted max-w-sm">
                        Create your first brief to anchor the Universe to what your brand cares about.
                    </p>
                    <Button
                        onClick={() => setModalOpen(true)}
                        className="mt-1"
                        data-testid="briefs-page-empty-new-button"
                    >
                        Create Brief
                    </Button>
                </div>
            ) : (
                <div
                    className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
                    data-testid="briefs-page-grid"
                >
                    {briefs.map((brief) => (
                        <BriefCard
                            key={brief.id}
                            brief={brief}
                            onOpenInClusters={handleOpenInClusters}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                        />
                    ))}
                </div>
            )}

            {!loading && briefs.length > 0 && briefs.length < total && (
                <div
                    className="flex items-center text-sm text-ink-muted"
                    data-testid="briefs-page-pagination"
                >
                    <span>
                        Showing {briefs.length} of {total} briefs
                    </span>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleLoadMore}
                        disabled={loadingMore}
                        className="ml-auto"
                        data-testid="briefs-page-load-more"
                    >
                        {loadingMore ? 'Loading…' : 'Load more'}
                    </Button>
                </div>
            )}

            <NewBriefModal
                isOpen={modalOpen || Boolean(editingBrief)}
                onClose={() => {
                    setModalOpen(false);
                    setEditingBrief(null);
                }}
                onSubmit={editingBrief ? handleUpdate : handleCreate}
                initialBrief={editingBrief}
            />
        </div>
    );
};

export default BriefsPage;
